import React from 'react';

interface CardProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  footer?: React.ReactNode;
  bordered?: boolean;
  elevated?: boolean;
  action?: React.ReactNode;
  animate?: boolean;
}

export const Card: React.FC<CardProps> = ({
  children,
  title,
  subtitle,
  className = '',
  headerClassName = '',
  bodyClassName = '',
  footerClassName = '',
  footer,
  bordered = true,
  elevated = false,
  action,
  animate = true,
}) => {
  const baseClasses = 'card rounded-2xl overflow-hidden relative';
  const animateClasses = animate ? 'animate-fade-in' : '';
  const elevationClasses = elevated 
    ? 'hover:scale-[1.01] transition-transform duration-300' 
    : '';
  
  return (
    <div className={`${baseClasses} ${elevationClasses} ${animateClasses} ${className}`}>
      {/* Animated gradient border overlay */}
      <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            background: 'linear-gradient(135deg, transparent 0%, rgba(6, 182, 242, 0.1) 50%, transparent 100%)',
          }}
        />
      </div>
      
      {(title || subtitle || action) && (
        <div 
          className={`relative px-6 py-5 border-b border-white/5 ${headerClassName}`}
          style={{
            background: 'linear-gradient(135deg, rgba(6, 182, 242, 0.08) 0%, rgba(0, 31, 89, 0.15) 50%, transparent 100%)'
          }}
        >
          <div className="flex justify-between items-start">
            <div>
              {title && (
                <h3 className="text-xl font-bold text-white tracking-tight">
                  {title}
                </h3>
              )}
              {subtitle && (
                <p className="text-sm text-gray-400 mt-1.5 font-medium">
                  {subtitle}
                </p>
              )}
            </div>
            {action && <div className="ml-4">{action}</div>}
          </div>
        </div>
      )}
      
      <div className={`relative px-6 py-6 ${bodyClassName}`}>
        {children}
      </div>
      
      {footer && (
        <div 
          className={`relative px-6 py-4 border-t border-white/5 ${footerClassName}`}
          style={{
            background: 'linear-gradient(180deg, transparent 0%, rgba(0, 31, 89, 0.1) 100%)'
          }}
        >
          {footer}
        </div>
      )}
    </div>
  );
};
